/**
 * <SchoolAssessmentDetail> — the expanded body of an assessment accordion (molecule, L0.1).
 *
 * Domain-aware tier: takes an `Assessment` and renders its chapter-by-chapter progress
 * (each with a mini `<ProgressBar>` colored by threshold) plus the action row. Composes DS
 * atoms/molecules only — `<Divider>`, `<Text>`, `<ProgressBar>`, `<LabeledIconButton>`.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { LabeledIconButton } from "../../atoms/Buttons/LabeledIconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types"

/** Chapter progress → fill color by threshold. */
function chapterColor(progress: number): SemanticColorKey {
    if (progress >= 70) return "success"
    if (progress >= 40) return "warning"
    return "danger"
}

export type SchoolAssessmentDetailProps = {
    assessment: UISchoolAssessment
    onEdit?: () => void
    onDelete?: () => void
    onDetails?: () => void
    /**
     * Écrit « À réviser » en toutes lettres au lieu de le cacher derrière le bouton « Détails ».
     * Défaut `false` : rendu inchangé dans l'accordéon, où la place manque. La feuille de détail,
     * elle, a la place — et un bouton qui ne fait qu'afficher une phrase déjà disponible n'a pas
     * lieu d'être.
     */
    showKeyRequirements?: boolean
    /**
     * Un nom de chapitre long passe à la ligne au lieu d'être coupé. Défaut `false` : une ligne,
     * comme avant. L'espace élève le pose : sa carte d'aujourd'hui écrit le nom en entier.
     */
    wrapChapterNames?: boolean
    /** Retrait horizontal — `"4"` par défaut. `"0"` quand la surface d'accueil en pose déjà un. */
    p?: SpacingKey
    testID?: string
}

export function SchoolAssessmentDetail(props: SchoolAssessmentDetailProps) {
    const {
        assessment,
        onEdit,
        onDelete,
        onDetails,
        showKeyRequirements = false,
        wrapChapterNames = false,
        p = "4",
        testID,
    } = props
    const { chaptersProgress, keyRequirements } = assessment
    const hasRequirements = keyRequirements != null && keyRequirements !== ""

    return (
        <Stack gap="3" style={styles.container(p)} testID={testID}>
            <Divider spacing="0" />
            {showKeyRequirements && hasRequirements && (
                <Stack gap="1">
                    <Text size="text-sm" color="text-muted">
                        À réviser
                    </Text>
                    <Text>{keyRequirements}</Text>
                </Stack>
            )}
            {chaptersProgress.map((item) => (
                <Row key={item.chapter.name} justify="between" align="center" gap="3">
                    <Text style={styles.shrink} numberOfLines={wrapChapterNames ? 0 : 1}>
                        {item.chapter.name}
                    </Text>
                    <Row gap="2" align="center">
                        <ProgressBar value={item.progress} color={chapterColor(item.progress)} style={styles.miniBar} />
                        <Text size="text-sm" color="text-muted">
                            {item.progress}%
                        </Text>
                    </Row>
                </Row>
            ))}
            <Divider spacing="0" />
            <Row gap="2" justify="end" align="center" style={styles.actions}>
                {hasRequirements && !showKeyRequirements && onDetails != null && (
                    <LabeledIconButton
                        label="Détails"
                        iconName="info-circle"
                        variant="outline"
                        size="sm"
                        onPress={onDetails}
                    />
                )}
                {onEdit != null && (
                    <LabeledIconButton label="Modifier" iconName="edit" variant="outline" size="sm" onPress={onEdit} />
                )}
                {onDelete != null && (
                    <LabeledIconButton
                        label="Supprimer"
                        iconName="trash"
                        variant="outline"
                        color="danger"
                        size="sm"
                        onPress={onDelete}
                    />
                )}
            </Row>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    container: (p: SpacingKey) => ({
        paddingHorizontal: theme.spacing[p],
        paddingBottom: theme.spacing[p],
    }),
    shrink: {
        flexShrink: 1,
    },
    miniBar: {
        width: 80,
    },
    /** Deux boutons ne tiennent pas côte à côte dans une feuille de 90 % à 390 px. */
    actions: {
        flexWrap: "wrap",
    },
}))
